import { useState, type FormEvent } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { money, fmtDate } from "@/lib/format";
import AddressFields, {
  type AddressValues,
  emptyAddress,
  addressFromJson,
  addressToJson,
  formatAddress,
} from "@/components/feature/AddressFields";
import { canManageCustomers } from "@/lib/permissions";

interface Customer {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  billing_address: Record<string, string> | null;
  shipping_address: Record<string, string> | null;
  status: string;
  notes: string | null;
  type: string;
  customer_reference: string | null;
}

interface SoRow {
  id: string;
  order_number: string;
  status: string;
  total: number | null;
  order_date: string | null;
}

interface ReturnRow {
  id: string;
  return_number: string;
  status: string;
  created_at: string | null;
}

interface RefundRow {
  id: string;
  amount: number;
  currency: string;
  status: string;
  reason: string | null;
  created_at: string | null;
}

interface CreditRow {
  id: string;
  amount: number;
  currency: string;
  status: string;
  balance_remaining: number;
  reason: string | null;
  created_at: string | null;
}

type Tab = "orders" | "returns" | "refunds" | "credits";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

const OPEN_STATUSES = [
  "draft",
  "confirmed",
  "allocated",
  "partially_allocated",
  "ready_to_pick",
  "partially_fulfilled",
];

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const canManage = canManageCustomers(role);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [statusError, setStatusError] = useState("");
  const [tab, setTab] = useState<Tab>("orders");
  const [orderLimit, setOrderLimit] = useState(50);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    billing: emptyAddress(),
    shipping: emptyAddress(),
    notes: "",
    status: "active",
    type: "individual",
    customer_reference: "",
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["customer-detail", org?.id, id, orderLimit],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const [customer, orders, totalOrders, openOrders, returns, credits] = await Promise.all([
        supabase.from("customers").select("*").eq("org_id", org!.id).eq("id", id).maybeSingle(),
        supabase
          .from("sales_orders")
          .select("id, order_number, status, total, order_date")
          .eq("org_id", org!.id)
          .eq("customer_id", id)
          .order("created_at", { ascending: false })
          .limit(orderLimit),
        supabase
          .from("sales_orders")
          .select("id", { count: "exact", head: true })
          .eq("org_id", org!.id)
          .eq("customer_id", id),
        supabase
          .from("sales_orders")
          .select("id", { count: "exact", head: true })
          .eq("org_id", org!.id)
          .eq("customer_id", id)
          .in("status", OPEN_STATUSES),
        supabase.from("sales_returns").select("id, return_number, status, created_at").eq("org_id", org!.id).eq("customer_id", id).order("created_at", { ascending: false }),
        supabase.from("customer_credits").select("id, amount, currency, status, balance_remaining, reason, created_at").eq("org_id", org!.id).eq("customer_id", id).order("created_at", { ascending: false }),
      ]);

      const orderIds = (orders.data ?? []).map((o: SoRow) => o.id);
      const returnIds = (returns.data ?? []).map((r: ReturnRow) => r.id);

      const [refundsByOrder, refundsByReturn] = await Promise.all([
        orderIds.length ? supabase.from("sales_refunds").select("id, amount, currency, status, reason, created_at").in("sales_order_id", orderIds) : Promise.resolve({ data: [] }),
        returnIds.length ? supabase.from("sales_refunds").select("id, amount, currency, status, reason, created_at").in("return_id", returnIds) : Promise.resolve({ data: [] }),
      ]);

      const refundMap = new Map<string, RefundRow>();
      [...(refundsByOrder.data ?? []), ...(refundsByReturn.data ?? [])].forEach((r: RefundRow) => refundMap.set(r.id, r));

      return {
        customer: (customer.data ?? null) as Customer | null,
        orders: (orders.data ?? []) as SoRow[],
        totalCount: totalOrders.count ?? 0,
        openCount: openOrders.count ?? 0,
        returns: (returns.data ?? []) as ReturnRow[],
        refunds: Array.from(refundMap.values()).sort((a, b) => String(b.created_at ?? "").localeCompare(String(a.created_at ?? ""))),
        credits: (credits.data ?? []) as CreditRow[],
      };
    },
  });

  const openEdit = () => {
    if (!data?.customer) return;
    const c = data.customer;
    setForm({
      name: c.name,
      email: c.email ?? "",
      phone: c.phone ?? "",
      billing: addressFromJson(c.billing_address),
      shipping: addressFromJson(c.shipping_address),
      notes: c.notes ?? "",
      status: c.status,
      type: c.type ?? "individual",
      customer_reference: c.customer_reference ?? "",
    });
    setError("");
    setEditOpen(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const { error: updErr } = await supabase
      .from("customers")
      .update({
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        billing_address: addressToJson(form.billing),
        shipping_address: addressToJson(form.shipping),
        notes: form.notes || null,
        status: form.status,
        type: form.type || "individual",
        customer_reference: form.customer_reference.trim() || null,
      })
      .eq("id", id);
    setSaving(false);
    if (updErr) {
      setError(updErr.code === "23505" ? "A customer with this email already exists." : updErr.message);
      return;
    }
    setEditOpen(false);
    queryClient.invalidateQueries({ queryKey: ["customer-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["customers", org?.id] });
  };

  const toggleStatus = async () => {
    if (!data?.customer) return;
    const next = data.customer.status === "active" ? "inactive" : "active";
    setStatusError("");
    setSaving(true);
    const { error: updErr } = await supabase.from("customers").update({ status: next }).eq("id", id);
    setSaving(false);
    if (updErr) {
      setStatusError(updErr.message);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["customer-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["customers", org?.id] });
  };

  if (isLoading) return <LoadingState />;

  if (isError || !data?.customer) {
    return (
      <div>
        <div className="mb-6">
          <button onClick={() => navigate("/customers")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 whitespace-nowrap">
            <i className="ri-arrow-left-line" /> Back to customers
          </button>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load this customer.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      </div>
    );
  }

  const c = data.customer;
  const billing = formatAddress(c.billing_address);
  const shipping = formatAddress(c.shipping_address);
  const availableCredit = data.credits
    .filter((cr) => cr.status !== "used")
    .reduce((sum, cr) => sum + Number(cr.balance_remaining ?? 0), 0);

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "orders", label: "Orders", count: data.orders.length },
    { key: "returns", label: "Returns", count: data.returns.length },
    { key: "refunds", label: "Refunds", count: data.refunds.length },
    { key: "credits", label: "Credits", count: data.credits.length },
  ];

  return (
    <div>
      <div className="mb-6">
        <button onClick={() => navigate("/customers")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 whitespace-nowrap">
          <i className="ri-arrow-left-line" /> Back to customers
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
              <span className="text-emerald-700 font-semibold text-lg">{c.name.charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900">{c.name}</h1>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full capitalize">{c.type}</span>
                <StatusBadge status={c.status} />
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {c.email ?? "—"}{c.phone ? ` · ${c.phone}` : ""}
              </p>
              {c.customer_reference && (
                <p className="text-xs text-slate-400 mt-0.5 font-mono">Ref: {c.customer_reference}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canManage && (
              <button onClick={openEdit} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                <i className="ri-pencil-line" /> Edit
              </button>
            )}
            {canManage && (
              <button
                onClick={toggleStatus}
                disabled={saving}
                className={`px-4 py-2 border text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2 disabled:opacity-60 ${
                  c.status === "active"
                    ? "bg-white border-slate-200 hover:bg-red-50 hover:text-red-600 text-slate-700"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white border-transparent"
                }`}
              >
                <i className={c.status === "active" ? "ri-user-unfollow-line" : "ri-user-follow-line"} />
                {c.status === "active" ? "Deactivate" : "Activate"}
              </button>
            )}
          </div>
        </div>

        {statusError && (
          <div className="mt-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
            {statusError}
          </div>
        )}

        {(billing || shipping || c.notes) && (
          <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-4">
            {billing && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Billing address</p>
                <p className="text-sm text-slate-700">{billing}</p>
              </div>
            )}
            {shipping && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Shipping address</p>
                <p className="text-sm text-slate-700">{shipping}</p>
              </div>
            )}
            {c.notes && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Notes</p>
                <p className="text-sm text-slate-700">{c.notes}</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-3 gap-4">
          <div>
            <p className="text-2xl font-bold text-slate-900">{data.totalCount}</p>
            <p className="text-xs text-slate-500 mt-0.5">Orders</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{data.openCount}</p>
            <p className="text-xs text-slate-500 mt-0.5">Open orders</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{money(availableCredit)}</p>
            <p className="text-xs text-slate-500 mt-0.5">Available credit</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-2 border-b border-slate-100">
          <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  tab === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {t.label}
                {t.count > 0 && <span className="ml-1.5 text-xs text-slate-400">{t.count}</span>}
              </button>
            ))}
          </div>
        </div>

        {tab === "orders" && (
          data.orders.length === 0 ? (
            <EmptyState title="No sales orders" description="Orders for this customer will appear here." />
          ) : (
            <>
              <div className="divide-y divide-slate-50">
                {data.orders.map((o) => (
                  <Link key={o.id} to={`/sales-orders/${o.id}`} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                    <div>
                      <p className="text-sm font-semibold text-slate-800 font-mono">{o.order_number}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{fmtDate(o.order_date)}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold text-slate-800">{money(o.total)}</span>
                      <StatusBadge status={o.status} />
                    </div>
                  </Link>
                ))}
              </div>
              {data.orders.length >= orderLimit && (
                <div className="px-4 py-3 border-t border-slate-100 text-center">
                  <button onClick={() => setOrderLimit((n) => n + 50)} className="text-sm text-emerald-700 hover:text-emerald-800 font-medium whitespace-nowrap">
                    Load more orders
                  </button>
                </div>
              )}
            </>
          )
        )}

        {tab === "returns" && (
          data.returns.length === 0 ? (
            <EmptyState title="No returns" description="Returns for this customer will appear here." />
          ) : (
            <div className="divide-y divide-slate-50">
              {data.returns.map((r) => (
                <Link key={r.id} to={`/returns/${r.id}`} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                  <div>
                    <p className="text-sm font-semibold text-slate-800 font-mono">{r.return_number}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{fmtDate(r.created_at)}</p>
                  </div>
                  <StatusBadge status={r.status} />
                </Link>
              ))}
            </div>
          )
        )}

        {tab === "refunds" && (
          data.refunds.length === 0 ? (
            <EmptyState title="No refunds" description="Refunds for this customer will appear here." />
          ) : (
            <div className="divide-y divide-slate-50">
              {data.refunds.map((r) => (
                <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{money(r.amount)}</p>
                    {r.reason && <p className="text-xs text-slate-400 mt-0.5">{r.reason}</p>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-slate-400">{fmtDate(r.created_at)}</span>
                    <StatusBadge status={r.status} />
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "credits" && (
          data.credits.length === 0 ? (
            <EmptyState title="No credits" description="Store credit for this customer will appear here." />
          ) : (
            <div className="divide-y divide-slate-50">
              {data.credits.map((cr) => (
                <div key={cr.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{money(cr.amount)}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {cr.reason ?? "Store credit"} · remaining {money(cr.balance_remaining)}
                    </p>
                  </div>
                  <StatusBadge status={cr.status} />
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit customer"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        }
      >
        <form onSubmit={handleSave} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div>
            <label className={labelCls}>Name *</label>
            <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <AddressFields label="Billing address" value={form.billing} onChange={(v) => setForm((f) => ({ ...f, billing: v }))} />
          <AddressFields label="Shipping address" value={form.shipping} onChange={(v) => setForm((f) => ({ ...f, shipping: v }))} />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Status</label>
              <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Type</label>
              <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className={inputCls}>
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Reference</label>
            <input value={form.customer_reference} onChange={(e) => setForm((f) => ({ ...f, customer_reference: e.target.value }))} className={inputCls} placeholder="Internal code" />
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} rows={3} maxLength={500} />
          </div>
        </form>
      </Modal>
    </div>
  );
}